// @ts-nocheck
import { editableConfig } from "./configFields.mjs";
// App.jsx — KitchenControl schil (multi-tenant: tenant volgt uit login)
// Modules: MEP + Recepten + Team + HACCP
// Iconen: lucide-react

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  ClipboardList,
  ShieldCheck,
  Sparkles,
  Thermometer,
  PackageCheck,
  Snowflake,
  TriangleAlert,
  Siren,
  HandHelping,
  Settings,
  Menu,
  Building2,
  FolderPlus,
  PlusCircle,
  FileText,
  Archive,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  BookOpen,
  ClipboardPaste,
  FolderCog,
  Users,
  LogOut,
  Mail,
  Clock,
  CloudSun,
  CalendarDays,
} from "lucide-react";
import { createClient } from "@supabase/supabase-js";
import HaccpModule from "./HaccpModule";
import TeamModule from "./TeamModule";
import ReceptenModule from "./ReceptenModule";
import MepModule from "./MepModule";
import SettingsModule from "./SettingsModule";
import KitchenDashboardShell from "./ui/KitchenDashboardShell";
import KitchenModuleShell from "./ui/KitchenModuleShell";
import { BeheerPinProvider } from "./ui/BeheerPin";
import { useWhiteboard, WhiteboardSheet } from "./Whiteboard";
import SignUp from "./SignUp";
import SubscriptionGate, { subscriptionState } from "./SubscriptionGate";
import { HelpButton, HelpPanel } from "./Help";
import { maakT, bepaalStartTaal, bewaarTaal, I18nProvider, useT, TALEN } from "./i18n";

// ══════════════════════════════════════════════════════════════════════════════
// CONFIG — per omgeving via Vite env-vars; valt terug op de huidige waarden.
//   Staging: zet VITE_SUPABASE_URL / VITE_SUPABASE_KEY naar je staging-project.
//   tenantId staat hier NIET meer — die volgt uit de ingelogde gebruiker.
// ══════════════════════════════════════════════════════════════════════════════
const CONFIG = {
  supabaseUrl:
    import.meta.env?.VITE_SUPABASE_URL ||
    "https://vvwgplljrzeycxlorfli.supabase.co",
  supabaseKey:
    import.meta.env?.VITE_SUPABASE_KEY ||
    "sb_publishable_zETvKuK1K2Lhh2waE5uoPw_seq-Ptf_",
};

// ── Kleuren (gedeeld palet) ───────────────────────────────────────────────────
const P = {
  bg: "#F8FAFC",
  navy: "#0F172A",
  navyMid: "#1E293B",
  navyLt: "#334155",
  txt: "#F1F5F9",
  txtMut: "#94A3B8",
  pri: "#2563EB",
  priBg: "#EFF6FF",
  sep: "#1E293B",
  // Lichte zijbalk (Fase 1)
  sbBg: "#FFFFFF",
  sbBorder: "#E2E8F0",
  sbText: "#334155",
  sbTextMut: "#64748B",
  sbHover: "#F1F5F9",
  sbActiveBg: "#EFF6FF",
  sbSub: "#F8FAFC",
};

// ── Module-definities ─────────────────────────────────────────────────────────
const MODULE_DEFS = [
  {
    id: "mep",
    label: "Mise En Place",
    Icon: ClipboardList,
    echt: true,
    subItems: [
      { id: "lijst",     Icon: ClipboardList, label: "Mep lijst",        desc: "Het dagelijkse overzicht: zie per afdeling en dag wat klaargemaakt moet worden en vink af." },
    ],
  },
  {
    id: "recepten",
    label: "Recepten",
    Icon: BookOpen,
    echt: true,
    subItems: [
      { id: "recepten",    Icon: BookOpen,       label: "Recepten",      desc: "Blader door al je recepten, bekijk, bewerk en print ze." },
      { id: "nieuw",       Icon: PlusCircle,     label: "Nieuw recept",  desc: "Maak een recept aan met ingrediënten, bereiding en allergenen.", beheerRol: true },
      { id: "paste",       Icon: ClipboardPaste, label: "Recept plakken", desc: "Plak een recept als tekst en laat het automatisch omzetten naar een net recept.", beheerRol: true },
      { id: "categorieen", Icon: FolderCog,      label: "Categorieën",   desc: "Beheer de categorieën waarin je recepten worden ingedeeld.", beheerRol: true },
    ],
  },
  {
    id: "team",
    label: "Team",
    Icon: Users,
    echt: true,
    subItems: [],
  },
  {
    id: "haccp",
    label: "HACCP",
    Icon: ShieldCheck,
    echt: true,
    subItems: [
      { id: "schoonmaak",   Icon: Sparkles,      label: "Schoonmaak",   desc: "Registreer uitgevoerde schoonmaaktaken." },
      { id: "temp",         Icon: Thermometer,   label: "Temperatuur",  desc: "Leg temperatuurmetingen vast van koelingen, vriezers en gerechten." },
      { id: "kalibratie",   Icon: Thermometer,   label: "Thermometercontrole", desc: "Controleer je steekthermometer en leg een eventuele meetcorrectie vast." },
      { id: "ontvangst",    Icon: PackageCheck,  label: "Ontvangst",    desc: "Registreer goederenontvangst en controleer je leveringen." },
      { id: "terugkoel",    Icon: Snowflake,     label: "Terugkoelen",  desc: "Volg het terugkoelen van gerechten binnen de NVWA-norm." },
      { id: "allergen",     Icon: TriangleAlert, label: "Allergenen",   desc: "Houd allergeneninformatie van je gerechten bij." },
      { id: "afwijking",    Icon: Siren,         label: "Afwijkingen",  desc: "Leg afwijkingen en corrigerende maatregelen vast." },
      { id: "hygiene",      Icon: HandHelping,   label: "Hygiëne",      desc: "Registreer je hygiënecontroles." },
    ],
  },
  {
    id: "settings",
    label: "Instellingen",
    Icon: Settings,
    echt: true,
    subItems: [],
  },
];

// ── Sub-item stijl ────────────────────────────────────────────────────────────
// Tijdsgebonden begroeting (op basis van de kloktijd van het apparaat)
function groet() {
  const u = new Date().getHours();
  if (u < 6) return "Welkom";
  if (u < 12) return "Goedemorgen";
  if (u < 18) return "Goedemiddag";
  return "Goedenavond";
}

const DASH_DAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

function dashIsoWeekOf(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return { year: d.getUTCFullYear(), week: Math.ceil(((d - yearStart) / 86400000 + 1) / 7) };
}

function dashTodayKey() {
  const now = new Date();
  const dayIdx = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const { year, week } = dashIsoWeekOf(now);
  return `${year}-W${String(week).padStart(2, "0")}-${DASH_DAYS[dayIdx]}`;
}


function dashHaccpPeriodeKey(freq, dateLike = new Date()) {
  const d = new Date(dateLike);
  if (isNaN(d.getTime())) return null;
  if (freq === "dagelijks") {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  if (freq === "wekelijks") {
    const { year, week } = dashIsoWeekOf(d);
    return `${year}-W${String(week).padStart(2, "0")}`;
  }
  if (freq === "vierweeks") {
    const dag = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const anker = new Date(2024, 0, 1);
    const dagen = Math.floor((dag - anker) / 86400000);
    return `P${Math.floor(dagen / 28)}`;
  }
  return null;
}

function dashHaccpSchoonmaakStatus(instellingen, records) {
  const taken = instellingen?.schoonmaakTaken || {};
  const alleRecords = Array.isArray(records) ? records : [];
  const openVoor = (freq) => {
    const lijst = Array.isArray(taken?.[freq]) ? taken[freq] : [];
    const periode = dashHaccpPeriodeKey(freq);
    const klaar = lijst.filter((taak) => alleRecords.some((r) => {
      if (String(r?.taak_id) !== String(taak?.id)) return false;
      if (r?.frequentie && r.frequentie !== freq) return false;
      return dashHaccpPeriodeKey(freq, r?.created_at) === periode;
    })).length;
    return Math.max(lijst.length - klaar, 0);
  };
  return {
    dagelijksOpen: openVoor("dagelijks"),
    wekelijksOpen: openVoor("wekelijks"),
    vierwekelijksOpen: openVoor("vierweeks"),
  };
}

function mepDashboardStats(data) {
  const dk = dashTodayKey();
  const items = (data?.items || []).filter((item) => !item.archived && (item.checks?.[dk] || 0) > 0);
  const klaar = items.filter((item) => (item.checks?.[dk] || 0) === 2).length;
  const open = items.filter((item) => (item.checks?.[dk] || 0) === 1).length;
  const afdelingen = data?.afdelingen || [];
  const klaarPerAfdeling = afdelingen
    .map((afd) => {
      const afdItems = items.filter((item) => item.afdeling_id === afd.id);
      const afdKlaar = afdItems.filter((item) => (item.checks?.[dk] || 0) === 2).length;
      return { naam: afd.naam, klaar: afdItems.length > 0 && afdKlaar === afdItems.length };
    })
    .filter((afd) => afd.klaar);
  return { open, klaar, totaal: items.length, klaarPerAfdeling };
}

function mepWhiteboardItems(data) {
  const now = new Date();
  const { year, week } = dashIsoWeekOf(now);
  const weekKey = `${year}-W${String(week).padStart(2, "0")}`;
  const weekPrefix = `${weekKey}-`;
  const geregeld = new Set(data?.whiteboard_geregeld?.[weekKey] || []);
  const map = new Map();

  (data?.items || []).filter((item) => !item.archived).forEach((item) => {
    const gepland = DASH_DAYS.some((dag) => (item.checks?.[`${weekPrefix}${dag}`] || 0) === 1);
    if (!gepland) return;
    (item.bestelartikelen || []).forEach((artikel) => {
      const naam = String(artikel?.naam || "").trim();
      if (!naam) return;
      const key = naam.toLowerCase();
      const huidig = map.get(key) || { naam, vias: new Set() };
      if (item.naam) huidig.vias.add(item.naam);
      map.set(key, huidig);
    });
  });

  return [...map.values()]
    .filter((item) => !geregeld.has(item.naam.trim().toLowerCase()))
    .map((item, index) => ({
      id: `dashboard-mep-${index}-${item.naam.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
      tekst: item.naam,
      via: [...item.vias].join(", "),
      bron: "mep",
      readonly: true,
      prioriteit: "mep",
      mepKey: item.naam.trim().toLowerCase(),
      weekKey,
    }));
}

function easterDate(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function dutchHolidayDates(year) {
  const pasen = easterDate(year);
  const koningsdag = new Date(year, 3, 27);
  if (koningsdag.getDay() === 0) koningsdag.setDate(26);
  return [
    { naam: "Nieuwjaarsdag", datum: new Date(year, 0, 1) },
    { naam: "Pasen", datum: pasen },
    { naam: "Tweede paasdag", datum: addDays(pasen, 1) },
    { naam: "Koningsdag", datum: koningsdag },
    { naam: "Hemelvaart", datum: addDays(pasen, 39) },
    { naam: "Pinksteren", datum: addDays(pasen, 49) },
    { naam: "Tweede pinksterdag", datum: addDays(pasen, 50) },
    { naam: "Kerstmis", datum: new Date(year, 11, 25) },
    { naam: "Tweede kerstdag", datum: new Date(year, 11, 26) },
  ];
}

function komendeFeestdagReminder(vensterDagen = 10) {
  const vandaag = new Date();
  const start = new Date(vandaag.getFullYear(), vandaag.getMonth(), vandaag.getDate());
  const jaren = [start.getFullYear(), start.getFullYear() + 1];
  return jaren
    .flatMap(dutchHolidayDates)
    .map(f => {
      const dag = new Date(f.datum.getFullYear(), f.datum.getMonth(), f.datum.getDate());
      return { ...f, dagen: Math.round((dag - start) / 86400000) };
    })
    .filter(f => f.dagen >= 0 && f.dagen <= vensterDagen)
    .sort((a, b) => a.dagen - b.dagen)[0] || null;
}

function dashboardWeekRange() {
  const now = new Date();
  const day = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  start.setDate(start.getDate() - day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  const { year, week } = dashIsoWeekOf(start);
  return { start, end, weekKey: `${year}-W${String(week).padStart(2, "0")}` };
}

const TERUGKOEL_MAX_MIN = 5 * 60;
function dashboardDuurTekst(min) {
  if (min === null || min === undefined) return "-";
  return `${Math.floor(min / 60)}u${String(min % 60).padStart(2, "0")}`;
}
function dashboardMinutenSindsStart(r, now = new Date()) {
  const basis = r?.created_at ? new Date(r.created_at) : null;
  if (!basis || isNaN(basis.getTime())) return null;
  return Math.max(0, Math.floor((now - basis) / (1000 * 60)));
}

const HACCP_WEEK_REMINDER_DEFS = [
  { id: "temp", label: "Temperatuur", table: "haccp_temp", subView: "temp" },
  { id: "ontvangst", label: "Ontvangst", table: "haccp_ontvangst", subView: "ontvangst" },
  { id: "terugkoel", label: "Terugkoelen", table: "haccp_terugkoel", subView: "terugkoel" },
];

function SubItem({ s, active, onClick }) {
  const Icon = s.Icon;
  const { t } = useT();
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "7px 12px 7px 32px",
        marginBottom: 1,
        borderRadius: 7,
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        fontSize: 12.5,
        background: active ? P.sbActiveBg : "transparent",
        color: active ? P.pri : P.sbTextMut,
        fontWeight: active ? 600 : 400,
        borderLeft: active ? `2px solid ${P.pri}` : "2px solid transparent",
        transition: "background 0.12s, color 0.12s",
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = P.sbHover; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    >
      <Icon size={14} strokeWidth={active ? 2.5 : 2} style={{ flexShrink: 0, opacity: active ? 1 : 0.7 }} />
      {t(s.label)}
    </button>
  );
}

// ── Module-overzicht: onderdelen als klikbare blokken (Instellingen-stijl) ──────
function ModuleOverzicht({ def, onBack, onPick, userRole }) {
  const { t } = useT();
  const alle = def.subItems || [];
  const subs = userRole === "medewerker" ? alle.filter((s) => !s.beheerRol) : alle;
  const hoofd = subs.filter((s) => !s.beheer);
  const beheer = subs.filter((s) => s.beheer);

  const Blok = ({ s }) => {
    const Icon = s.Icon;
    return (
      <button
        onClick={() => onPick(s.id)}
        style={{
          display: "flex", alignItems: "center", gap: 14, width: "100%",
          background: P.sbBg, border: `1px solid ${P.sbBorder}`, borderRadius: 14,
          padding: "16px 18px", marginBottom: 14, cursor: "pointer",
          textAlign: "left", fontFamily: "inherit",
          transition: "border-color .12s, box-shadow .12s, transform .12s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#BFDBFE"; e.currentTarget.style.boxShadow = "0 6px 18px -8px rgba(37,99,235,0.25)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = P.sbBorder; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
      >
        <div style={{ width: 40, height: 40, borderRadius: 11, background: P.priBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {Icon && <Icon size={20} color={P.pri} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15.5, fontWeight: 700, color: P.navy, marginBottom: 2 }}>{t(s.label)}</div>
          {s.desc && <div style={{ fontSize: 13, color: P.sbText, lineHeight: 1.45 }}>{t(s.desc)}</div>}
        </div>
        <ChevronRight size={20} color={P.txtMut} style={{ flexShrink: 0 }} />
      </button>
    );
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "clamp(20px, 4vw, 36px)" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <button onClick={onBack}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: P.sbTextMut, padding: 0, marginBottom: 18 }}
          onMouseEnter={(e) => { e.currentTarget.style.color = P.pri; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = P.sbTextMut; }}>
          <ChevronLeft size={15} /> {t("Beginpagina")}
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: P.navy, margin: "0 0 2px", letterSpacing: 0.2 }}>{t(def.label)}</h1>
        <p style={{ fontSize: 13.5, color: P.sbTextMut, margin: "0 0 22px" }}>{t("Kies een onderdeel om mee te werken.")}</p>
        {hoofd.map((s) => <Blok key={s.id} s={s} />)}
        {beheer.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: P.txtMut, margin: "10px 0 10px 2px" }}>{t("Beheer")}</div>
            {beheer.map((s) => <Blok key={s.id} s={s} />)}
          </>
        )}
      </div>
    </div>
  );
}

// ── Weer (Open-Meteo, keyless + CORS) ───────────────────────────────────
const WMO_WEER = {
  0: "Helder", 1: "Vrijwel onbewolkt", 2: "Half bewolkt", 3: "Bewolkt",
  45: "Mist", 48: "Mist",
  51: "Lichte motregen", 53: "Motregen", 55: "Dichte motregen",
  56: "IJzel", 57: "IJzel",
  61: "Lichte regen", 63: "Regen", 65: "Zware regen",
  66: "IJzel", 67: "IJzel",
  71: "Lichte sneeuw", 73: "Sneeuw", 75: "Zware sneeuw", 77: "Sneeuwkorrels",
  80: "Lichte buien", 81: "Buien", 82: "Zware buien",
  85: "Sneeuwbuien", 86: "Sneeuwbuien",
  95: "Onweer", 96: "Onweer met hagel", 99: "Onweer met hagel",
};
function weerInfo(code) { return WMO_WEER[code] || "Onbekend"; }

function WeerWidget({ plaats }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("laden"); // laden | klaar | fout
  const gemonteerd = useRef(true);
  useEffect(() => () => { gemonteerd.current = false; }, []);

  // Haalt het weer op. maxLeeftijd = hoe oud de cache mag zijn voordat we vers ophalen.
  // stilletjes = niet naar "laden" schakelen (voor verversen op de achtergrond).
  const haalWeer = useCallback(async (maxLeeftijd, stilletjes) => {
    if (!plaats) return;
    try {
      if (!stilletjes) setStatus("laden");
      // 1) Plaats -> coördinaten (gecachet; verandert niet)
      let geo;
      const geoKey = `kc-weer-geo-${plaats.toLowerCase().trim()}`;
      const geoCache = localStorage.getItem(geoKey);
      if (geoCache) {
        geo = JSON.parse(geoCache);
      } else {
        const gr = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(plaats)}&count=1&language=nl`);
        const gj = await gr.json();
        if (!gj.results || !gj.results[0]) { if (!stilletjes && gemonteerd.current) setStatus("fout"); return; }
        geo = { lat: gj.results[0].latitude, lon: gj.results[0].longitude, naam: gj.results[0].name };
        localStorage.setItem(geoKey, JSON.stringify(geo));
      }
      // 2) Weer (uit cache als die jonger is dan maxLeeftijd)
      const wKey = `kc-weer2-${geo.lat.toFixed(2)}-${geo.lon.toFixed(2)}`;
      const wCache = localStorage.getItem(wKey);
      if (wCache) {
        const c = JSON.parse(wCache);
        if (Date.now() - c.ts < maxLeeftijd) {
          if (gemonteerd.current) { setData({ ...c.data, naam: geo.naam }); setStatus("klaar"); }
          return;
        }
      }
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${geo.lat}&longitude=${geo.lon}&current=temperature_2m,wind_speed_10m,weather_code&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max&timezone=auto&forecast_days=1`;
      const r = await fetch(url);
      const j = await r.json();
      const d = {
        nu: Math.round(j.current.temperature_2m),
        wind: Math.round(j.current.wind_speed_10m),
        code: j.current.weather_code,        // huidig weerbeeld — zoals het nú buiten is
        max: Math.round(j.daily.temperature_2m_max[0]),
        min: Math.round(j.daily.temperature_2m_min[0]),
        neerslag: j.daily.precipitation_probability_max[0] ?? 0,
      };
      localStorage.setItem(wKey, JSON.stringify({ ts: Date.now(), data: d }));
      if (gemonteerd.current) { setData({ ...d, naam: geo.naam }); setStatus("klaar"); }
    } catch (e) { if (!stilletjes && gemonteerd.current) setStatus("fout"); }
  }, [plaats]);

  // Bij openen van de pagina: cache mag tot een uur oud zijn.
  useEffect(() => { haalWeer(3600000, false); }, [haalWeer]);

  // Bij terugkeer op het tabblad: ververs als het weer ouder is dan 15 minuten.
  useEffect(() => {
    const bijTerugkeer = () => {
      if (document.visibilityState === "visible") haalWeer(900000, true);
    };
    document.addEventListener("visibilitychange", bijTerugkeer);
    window.addEventListener("focus", bijTerugkeer);
    return () => {
      document.removeEventListener("visibilitychange", bijTerugkeer);
      window.removeEventListener("focus", bijTerugkeer);
    };
  }, [haalWeer]);

  const kaart = { background: "#fff", border: `1px solid ${P.sbBorder}`, borderRadius: 14, padding: "13px 16px", maxWidth: 460 };
  const { t } = useT();

  if (status === "laden") return <div style={{ ...kaart, fontSize: 13, color: P.sbTextMut }}>{t("Weer laden...")}</div>;
  if (status === "fout" || !data) return <div style={{ ...kaart, fontSize: 12.5, color: P.sbTextMut }}>{t("Weer kon niet geladen worden voor {plaats}. Controleer de plaatsnaam in Instellingen.", { plaats })}</div>;

  const oms = weerInfo(data.code);

  return (
    <div style={kaart}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: P.priBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <CloudSun size={23} color={P.pri} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: P.navy }}>{data.nu}&deg;</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: P.sbText }}>{data.naam}</span>
          </div>
          <div style={{ fontSize: 12, color: P.sbTextMut, marginTop: 1 }}>
            {oms} - max {data.max}&deg; - min {data.min}&deg; - regen {data.neerslag}% - wind {data.wind} km/u
          </div>
        </div>
      </div>
      <div style={{ fontSize: 10, color: P.txtMut, marginTop: 8, textAlign: "right" }}>{t("weer via Open-Meteo")}</div>
    </div>
  );
}

// ── Loginscherm (magic link, geen wachtwoord) ─────────────────────────────────
function LoginScreen({ supabaseClient, onNaarSignup }) {
  const [mode, setMode] = useState("welkom"); // "welkom" | "magic" | "password" | "reset"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // De achtergrondfoto moet tot de schermrand lopen. De browser zet standaard een
  // kleine marge op de body, waardoor je anders een witte rand rondom ziet. Zolang
  // dit scherm getoond wordt zetten we die marge op nul; bij verlaten herstellen we.
  useEffect(() => {
    const vorigeMargin = document.body.style.margin;
    const vorigePadding = document.body.style.padding;
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    return () => {
      document.body.style.margin = vorigeMargin;
      document.body.style.padding = vorigePadding;
    };
  }, []);

  const inputStyle = { width: "100%", boxSizing: "border-box", padding: "11px 13px", borderRadius: 9, border: "1px solid #CBD5E1", fontSize: 14, fontFamily: "inherit", marginBottom: 10 };
  const btnStyle = (disabled) => ({ width: "100%", padding: "11px 13px", borderRadius: 9, border: "none", background: P.pri, color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: "inherit", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.7 : 1 });
  const linkStyle = { display: "block", width: "100%", textAlign: "center", background: "none", border: "none", color: P.pri, fontSize: 12.5, fontFamily: "inherit", cursor: "pointer", padding: 0, marginTop: 12, textDecoration: "underline" };

  const sendMagic = async () => {
    if (!email || busy) return;
    setBusy(true);
    setErr("");
    const { error } = await supabaseClient.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: window.location.origin },
    });
    setBusy(false);
    if (error) setErr(error.message);
    else setSent(true);
  };

  const loginPassword = async () => {
    if (!email || !password || busy) return;
    setBusy(true);
    setErr("");
    const { error } = await supabaseClient.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setBusy(false);
    if (error) setErr(error.message);
    // Bij succes zet onAuthStateChange de sessie -> de app gaat vanzelf door.
  };

  const resetWachtwoord = async () => {
    if (!email || busy) return;
    setBusy(true);
    setErr("");
    const { error } = await supabaseClient.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin,
    });
    setBusy(false);
    if (error) setErr(error.message);
    else setSent(true);
  };

  return (
    <div style={{ position: "relative", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: P.bg, fontFamily: "system-ui,-apple-system,sans-serif", padding: 20, overflow: "hidden" }}>
      {/* Sfeer-achtergrond: geblurde keukenfoto + lichte donkere waas, zodat het
          witte kaartje mooi uitspringt. De laag is iets groter dan het scherm
          (negatieve inset) zodat de blur geen vage rand langs de zijkanten geeft. */}
      <div aria-hidden style={{
        position: "absolute", inset: -20, zIndex: 0,
        backgroundImage: "url('/keuken-achtergrond.jpg')",
        backgroundSize: "cover", backgroundPosition: "center",
        filter: "blur(4px)",
      }} />
      <div aria-hidden style={{ position: "absolute", inset: 0, zIndex: 0, background: "rgba(15,23,42,0.55)" }} />
      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 380, background: "#fff", borderRadius: 14, padding: 28, boxShadow: "0 10px 30px rgba(15,23,42,0.25)" }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: P.navy, marginBottom: 6 }}>KitchenControl</div>

        {sent ? (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: P.pri, fontWeight: 600, marginBottom: 8 }}>
              <Mail size={18} /> Check je mail
            </div>
            <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.5, margin: 0 }}>
              {mode === "reset"
                ? <>We hebben een herstellink gestuurd naar <b>{email}</b>. Klik erop om een nieuw wachtwoord in te stellen.</>
                : <>We hebben een inloglink gestuurd naar <b>{email}</b>. Klik op de link om in te loggen.</>}
            </p>
          </div>
        ) : mode === "welkom" ? (
          <div>
            <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.5, marginTop: 0, marginBottom: 18 }}>
              Mise en Place, Recepten, Haccp en Keukenrooster — alles voor je keuken op één plek.
            </p>
            <button onClick={() => onNaarSignup && onNaarSignup()} style={btnStyle(false)}>
              Start een proefaccount
            </button>
            <div style={{ borderTop: `1px solid ${P.sbBorder}`, marginTop: 18, paddingTop: 16, textAlign: "center" }}>
              <div style={{ fontSize: 12.5, color: "#64748B", marginBottom: 10 }}>Heb je al een account?</div>
              <button onClick={() => { setMode("magic"); setErr(""); }}
                style={{ width: "100%", padding: "10px 13px", borderRadius: 9, border: `1px solid ${P.sbBorder}`, background: "#fff", color: P.navy, fontSize: 13.5, fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}>
                Inloggen
              </button>
            </div>
          </div>
        ) : mode === "magic" ? (
          <div>
            <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.5, marginTop: 0, marginBottom: 16 }}>
              Vul je e-mailadres in. Je krijgt een inloglink toegestuurd.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") sendMagic(); }}
              placeholder="naam@bedrijf.nl"
              style={inputStyle}
            />
            {err && <div style={{ color: "#DC2626", fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
            <button onClick={sendMagic} disabled={busy} style={btnStyle(busy)}>
              {busy ? "Bezig…" : "Stuur inloglink"}
            </button>
            <button onClick={() => { setMode("password"); setErr(""); }} style={linkStyle}>
              Inloggen met wachtwoord
            </button>
          </div>
        ) : mode === "reset" ? (
          <div>
            <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.5, marginTop: 0, marginBottom: 16 }}>
              Vul je e-mailadres in. Je krijgt een link om een nieuw wachtwoord in te stellen.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") resetWachtwoord(); }}
              placeholder="naam@bedrijf.nl"
              style={inputStyle}
            />
            {err && <div style={{ color: "#DC2626", fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
            <button onClick={resetWachtwoord} disabled={busy} style={btnStyle(busy)}>
              {busy ? "Bezig…" : "Stuur herstellink"}
            </button>
            <button onClick={() => { setMode("password"); setErr(""); }} style={linkStyle}>
              Terug naar inloggen
            </button>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.5, marginTop: 0, marginBottom: 16 }}>
              Log in met je e-mailadres en wachtwoord.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="naam@bedrijf.nl"
              style={inputStyle}
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") loginPassword(); }}
              placeholder="wachtwoord"
              style={inputStyle}
            />
            {err && <div style={{ color: "#DC2626", fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
            <button onClick={loginPassword} disabled={busy} style={btnStyle(busy)}>
              {busy ? "Bezig…" : "Inloggen"}
            </button>
            <button onClick={() => { setMode("magic"); setErr(""); }} style={linkStyle}>
              Inloggen met inloglink
            </button>
            <button onClick={() => { setMode("reset"); setErr(""); }} style={{ ...linkStyle, marginTop: 6, color: "#64748B" }}>
              Wachtwoord vergeten?
            </button>
          </div>
        )}

        {!sent && mode !== "welkom" && (
          <div style={{ borderTop: `1px solid ${P.sbBorder}`, marginTop: 18, paddingTop: 14, textAlign: "center" }}>
            <button onClick={() => { setMode("welkom"); setErr(""); }} style={{ background: "none", border: "none", color: "#64748B", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", padding: 0 }}>
              ← Terug
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Nieuw wachtwoord instellen (na herstellink) ───────────────────────────────
function NieuwWachtwoordScherm({ supabaseClient, onKlaar }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [klaar, setKlaar] = useState(false);

  const inputStyle = { width: "100%", boxSizing: "border-box", padding: "11px 13px", borderRadius: 9, border: "1px solid #CBD5E1", fontSize: 14, fontFamily: "inherit", marginBottom: 10 };
  const btnStyle = (disabled) => ({ width: "100%", padding: "11px 13px", borderRadius: 9, border: "none", background: P.pri, color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: "inherit", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.7 : 1 });

  const opslaan = async () => {
    if (busy) return;
    if (pw.length < 8) { setErr("Gebruik minstens 8 tekens."); return; }
    if (pw !== pw2) { setErr("De wachtwoorden komen niet overeen."); return; }
    setBusy(true);
    setErr("");
    const { error } = await supabaseClient.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setKlaar(true);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: P.bg, fontFamily: "system-ui,-apple-system,sans-serif", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 380, background: "#fff", borderRadius: 14, padding: 28, boxShadow: "0 10px 30px rgba(15,23,42,0.08)" }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: P.navy, marginBottom: 6 }}>KitchenControl</div>
        {klaar ? (
          <div>
            <div style={{ color: P.pri, fontWeight: 600, marginBottom: 8 }}>Wachtwoord gewijzigd</div>
            <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.5, margin: "0 0 16px" }}>
              Je nieuwe wachtwoord is opgeslagen. Je bent meteen ingelogd.
            </p>
            <button onClick={() => onKlaar && onKlaar()} style={btnStyle(false)}>Ga naar de app</button>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.5, marginTop: 0, marginBottom: 16 }}>
              Kies een nieuw wachtwoord voor je account.
            </p>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="nieuw wachtwoord" style={inputStyle} />
            <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") opslaan(); }} placeholder="herhaal wachtwoord" style={inputStyle} />
            {err && <div style={{ color: "#DC2626", fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
            <button onClick={opslaan} disabled={busy} style={btnStyle(busy)}>
              {busy ? "Bezig…" : "Wachtwoord opslaan"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Statusscherm (laden / geen omgeving / fout) ───────────────────────────────
function Status({ children, onSignOut }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: P.bg, color: "#475569", fontSize: 14, fontFamily: "system-ui,-apple-system,sans-serif", gap: 14, padding: 20, textAlign: "center" }}>
      <div style={{ maxWidth: 420 }}>{children}</div>
      {onSignOut && (
        <button onClick={onSignOut} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", color: "#475569", fontSize: 13, fontFamily: "inherit", cursor: "pointer" }}>
          <LogOut size={14} /> Uitloggen
        </button>
      )}
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  // Eén Supabase-client (beheert sessie + realtime)
  const supabaseClient = useMemo(
    () => createClient(CONFIG.supabaseUrl, CONFIG.supabaseKey),
    []
  );

  // Auth
  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [authScherm, setAuthScherm] = useState("login"); // "login" | "signup"
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [inviteCode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("invite"); } catch { return null; }
  });
  const [tenantId, setTenantId] = useState(null);
  const [tenantError, setTenantError] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // App-state
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Taal (meertaligheid). Begint bij een opgeslagen keuze, anders de browsertaal.
  const [lang, setLang] = useState(() => bepaalStartTaal());
  const t = useMemo(() => maakT(lang), [lang]);
  const setTaal = (l) => { setLang(l); bewaarTaal(l); };

  const [activeModule, setActiveModule] = useState(null);
  const [subView, setSubView] = useState(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [overlayActief, setOverlayActief] = useState(false);
  const [openReceptId, setOpenReceptId] = useState(null);
  const [coolingRequest, setCoolingRequest] = useState(null);
  // Onthoudt vanwaar een recept werd geopend (bijv. de Mep-lijst), zodat de
  // gebruiker er met één klik naar terug kan. null = geen herkomst actief.
  const [receptHerkomst, setReceptHerkomst] = useState(null);
  const [narrow, setNarrow] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpTopic, setHelpTopic] = useState(null);
  const [dashboard, setDashboard] = useState({ mep: { open: 0, klaar: 0, totaal: 0, klaarPerAfdeling: [] }, mepWhiteboard: [], mepData: null });
  const [haccpReminders, setHaccpReminders] = useState([]);
  const [haccpSchoonmaakDashboard, setHaccpSchoonmaakDashboard] = useState({ dagelijksOpen: 0, wekelijksOpen: 0, vierwekelijksOpen: 0 });
  const [openTerugkoelingen, setOpenTerugkoelingen] = useState([]);
  const [toonAlleHaccpReminders, setToonAlleHaccpReminders] = useState(false);
  const [toonAlleTerugkoelingen, setToonAlleTerugkoelingen] = useState(false);
  const [dashboardNow, setDashboardNow] = useState(new Date());
  const [receptDashboard, setReceptDashboard] = useState({ totaal: 0, laatst: "" });
  const [teamDashboard, setTeamDashboard] = useState([]);
  const [whiteboardOpen, setWhiteboardOpen] = useState(false);

  // ── Geauthenticeerde fetch + gedeelde sb-helper ────────────────────────────
  const authedFetch = useCallback(async (path, opts = {}) => {
    const { data: { session: s } } = await supabaseClient.auth.getSession();
    const token = s?.access_token || CONFIG.supabaseKey;
    return fetch(`${CONFIG.supabaseUrl}/rest/v1/${path}`, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        apikey: CONFIG.supabaseKey,
        Authorization: `Bearer ${token}`,
        ...(opts.headers || {}),
      },
    });
  }, [supabaseClient]);

  const sb = useMemo(() => ({
    selectStrict: async (table, params = "") => {
      const r = await authedFetch(`${table}?${params}`);
      if (!r.ok) throw new Error("Registraties konden niet worden geladen.");
      return await r.json();
    },
    select: async (table, params = "") => {
      try { const r = await authedFetch(`${table}?${params}`); return r.ok ? await r.json() : []; }
      catch { return []; }
    },
    insert: async (table, body) => {
      try {
        const r = await authedFetch(table, { method: "POST", headers: { Prefer: "return=representation" }, body: JSON.stringify(body) });
        if (!r.ok) return null;
        const d = await r.json();
        return Array.isArray(d) ? d[0] : d;
      } catch { return null; }
    },
    update: async (table, filter, body) => {
      try { await authedFetch(`${table}?${filter}`, { method: "PATCH", headers: { Prefer: "return=minimal" }, body: JSON.stringify(body) }); } catch {}
    },
    remove: async (table, filter) => {
      try { await authedFetch(`${table}?${filter}`, { method: "DELETE" }); } catch {}
    },
  }), [authedFetch]);

  // Whiteboard blijft dezelfde bestaande functie; het dashboard opent alleen dezelfde sheet.
  const whiteboard = useWhiteboard(sb, tenantId, supabaseClient);

  // ── Responsive ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const check = () => setNarrow(window.innerWidth <= 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ── Sessie volgen ──────────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    supabaseClient.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setAuthReady(true);
    });
    const { data: sub } = supabaseClient.auth.onAuthStateChange((event, s) => {
      if (event === "PASSWORD_RECOVERY") setRecoveryMode(true);
      setSession(s);
    });
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, [supabaseClient]);

  // ── Tenant resolven uit membership na login ────────────────────────────────
  useEffect(() => {
    if (!session) { setTenantId(null); setTenantError(null); setUserRole(null); return; }
    let mounted = true;
    (async () => {
      // realtime ook als ingelogde gebruiker laten draaien (zodat RLS geldt)
      try { supabaseClient.realtime.setAuth(session.access_token); } catch {}

      // Haal de membership(s) van de ingelogde gebruiker op.
      const haalLeden = () => supabaseClient.from("memberships").select("tenant_id, role");

      let { data, error } = await haalLeden();
      if (!mounted) return;
      if (error) { setTenantError("Kon je omgeving niet laden. Probeer opnieuw in te loggen."); return; }

      // Nog geen membership? Dan kan dit de EERSTE login van een uitgenodigde
      // collega zijn. Probeer de openstaande uitnodiging te verzilveren; lukt dat,
      // dan ontstaat er een membership en halen we 'm opnieuw op.
      if (!data?.length) {
        try {
          const { data: lid } = await supabaseClient.rpc("claim_uitnodiging");
          if (lid) {
            const opnieuw = await haalLeden();
            if (!mounted) return;
            data = opnieuw.data;
          }
        } catch (e) {
          // Stil: geen uitnodiging of claim mislukt → valt hieronder in de nette melding.
        }
      }

      if (!data?.length) { setTenantId(null); setTenantError("Je account is nog niet gekoppeld aan een omgeving. Neem contact op met de beheerder."); return; }
      setTenantId(data[0].tenant_id);
      setUserRole(data[0].role || null);
      setTenantError(null);
    })();
    return () => { mounted = false; };
  }, [session, supabaseClient]);

  // ── Lokale cache wissen bij tenantwissel ────────────────────────────────────
  // De modules cachen in localStorage onder "kc-"-sleutels. Die cache is per
  // browser, niet per tenant. Logt er een andere tenant in (of een nieuwe klant
  // in dezelfde browser), dan wissen we die cache zodat de nieuwe omgeving
  // schoon laadt vanuit de database. De Supabase-sessie ("sb-"-sleutels) blijft
  // staan, dus je blijft ingelogd.
  useEffect(() => {
    if (!tenantId) return;
    const MARKER = "kc_active_tenant"; // underscore -> valt buiten het wissen
    if (localStorage.getItem(MARKER) !== tenantId) {
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith("kc-")) localStorage.removeItem(k);
        }
        localStorage.setItem(MARKER, tenantId);
      } catch {}
    }
  }, [tenantId]);

  // ── app_config laden zodra we de tenant kennen ─────────────────────────────
  useEffect(() => {
    if (!tenantId) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      const rows = await sb.select("app_config", `tenant_id=eq.${tenantId}&select=*&limit=1`);
      if (!mounted) return;
      setConfig(rows?.length ? rows[0] : { tenant_id: tenantId, bedrijfsnaam: "KitchenControl", modules: {} });
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [tenantId, sb]);

  useEffect(() => {
    if (!tenantId) return;
    let mounted = true;
    (async () => {
      const { start, end, weekKey } = dashboardWeekRange();
      const startIso = start.toISOString();
      const endIso = end.toISOString();
      const results = await Promise.all(HACCP_WEEK_REMINDER_DEFS.map(async (def) => {
        const rows = await sb.select(def.table, `tenant_id=eq.${tenantId}&created_at=gte.${startIso}&created_at=lte.${endIso}&select=id&limit=1`);
        return { ...def, weekKey, registered: (rows || []).length > 0 };
      }));
      if (!mounted) return;
      setHaccpReminders(results.filter(r => !r.registered));
    })();
    return () => { mounted = false; };
  }, [tenantId, sb]);


  // Dashboard: schoonmaaktaken gebruiken dezelfde kalender-/week-/4-wekenlogica als HACCP Schoonmaak.
  useEffect(() => {
    if (!tenantId) return;
    let mounted = true;
    const laden = async () => {
      const [cfgRows, records] = await Promise.all([
        sb.select("haccp_config", `tenant_id=eq.${tenantId}&select=instellingen&limit=1`),
        sb.select("haccp_schoonmaak", `tenant_id=eq.${tenantId}&select=id,taak_id,frequentie,created_at&order=created_at.desc&limit=500`),
      ]);
      if (!mounted) return;
      setHaccpSchoonmaakDashboard(dashHaccpSchoonmaakStatus(cfgRows?.[0]?.instellingen || {}, records || []));
    };
    laden();
    const timer = window.setInterval(laden, 60000);
    const onSchoonmaakChanged = () => laden();
    window.addEventListener("kc:haccp-schoonmaak-changed", onSchoonmaakChanged);
    return () => {
      mounted = false;
      window.clearInterval(timer);
      window.removeEventListener("kc:haccp-schoonmaak-changed", onSchoonmaakChanged);
    };
  }, [tenantId, sb]);

  useEffect(() => {
    if (!tenantId) return;
    let mounted = true;
    (async () => {
      const rows = await sb.select("mep_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
      if (!mounted) return;
      const mepData = rows?.[0]?.data;
      setDashboard((d) => ({ ...d, mep: mepDashboardStats(mepData), mepWhiteboard: mepWhiteboardItems(mepData), mepData }));
    })();
    return () => { mounted = false; };
  }, [tenantId, sb]);

  // KC_UI_V2_DASHBOARD_MEP_SYNC: directe lokale update vanuit MepModule.
  useEffect(() => {
    const onMepData = (event) => {
      const mepData = event?.detail;
      if (!mepData) return;
      setDashboard((d) => ({
        ...d,
        mep: mepDashboardStats(mepData),
        mepWhiteboard: mepWhiteboardItems(mepData),
        mepData,
      }));
    };
    window.addEventListener("kc:mep-data", onMepData);
    return () => window.removeEventListener("kc:mep-data", onMepData);
  }, []);

  // Dashboard: recepten zitten als JSON in recepten_data.data.
  useEffect(() => {
    if (!tenantId) return;
    let mounted = true;
    (async () => {
      const rows = await sb.select("recepten_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
      if (!mounted) return;
      const recepten = Array.isArray(rows?.[0]?.data?.recepten) ? rows[0].data.recepten : [];
      const actief = recepten.filter((r) => !r?.archived);
      setReceptDashboard({
        totaal: actief.length,
        laatst: actief.length ? (actief[actief.length - 1]?.naam || "") : "",
      });
    })();
    return () => { mounted = false; };
  }, [tenantId, sb]);

  useEffect(() => {
    if (!tenantId) return;
    let mounted = true;
    (async () => {
      const rows = await sb.select("team_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
      if (!mounted) return;
      const medewerkers = Array.isArray(rows?.[0]?.data?.medewerkers) ? rows[0].data.medewerkers : [];
      setTeamDashboard(medewerkers.map((m, index) => ({
        id: m.id || `team-${index}`,
        naam: `${m.voornaam || ""} ${m.achternaam || ""}`.trim() || m.naam || "Teamlid",
      })));
    })();
    return () => { mounted = false; };
  }, [tenantId, sb]);

  useEffect(() => {
    if (!tenantId) return;
    let mounted = true;
    const laadTerugkoelingen = async () => {
      const rows = await sb.select("haccp_terugkoel", `tenant_id=eq.${tenantId}&select=id,product,start_tijd,start_temp,created_at,eind_tijd,eind_temp&order=created_at.desc&limit=100`);
      if (!mounted) return;
      const open = (rows || []).filter((r) => !r.eind_tijd || r.eind_temp === null || r.eind_temp === undefined || r.eind_temp === "");
      setOpenTerugkoelingen(open);
      setDashboardNow(new Date());
    };
    laadTerugkoelingen();
    const timer = window.setInterval(laadTerugkoelingen, 60000);
    return () => { mounted = false; window.clearInterval(timer); };
  }, [tenantId, sb]);

  // ── Geen module standaard geopend ───────────────────────────────────────────
  // Bij openen blijven alle menu's dicht; de gebruiker kiest zelf een module.
  // (De inhoud toont dan "Selecteer een module".)

  useEffect(() => {
    const def = MODULE_DEFS.find((m) => m.id === activeModule);
    if (!def) return;
    const subs = def.subItems || [];
    // Hoort de huidige subView niet bij deze module, of mag een medewerker er niet
    // bij? Terug naar het overzicht (null). We selecteren NOOIT automatisch het
    // eerste onderdeel — het overzicht is de landing.
    if (subView !== null && subView !== undefined) {
      const huidige = subs.find((s) => s.id === subView);
      if (!huidige || (userRole === "medewerker" && huidige.beheerRol)) {
        setSubView(null);
      }
    }
  }, [activeModule, subView, userRole]);

  // ── Herkomst wissen zodra de gebruiker het receptscherm verlaat ────────────
  // De "terug naar …"-knop hoort alleen te verschijnen zolang je het recept
  // bekijkt dat je vanuit (bijv.) de Mep-lijst opende. Ga je ergens anders heen,
  // dan vergeten we de herkomst zodat de knop niet blijft hangen.
  useEffect(() => {
    if (receptHerkomst && !(activeModule === "recepten" && subView === "recepten")) {
      setReceptHerkomst(null);
    }
  }, [activeModule, subView, receptHerkomst]);

  const handleConfigSave = async (newConfig) => {
    setConfig(newConfig);
    // Alleen bewerkbare velden wegschrijven (id/created_at/updated_at niet patchen)
    const velden = editableConfig(newConfig);
    velden.tenant_id = tenantId;
    // Bestaat er al een app_config-rij voor deze tenant? Zo niet: aanmaken i.p.v.
    // patchen. Een PATCH op een niet-bestaande rij raakt 0 rijen en slaat dus
    // niets op — dat was de bug: tenants zonder app_config-rij onthielden niets.
    const bestaand = await sb.select("app_config", `tenant_id=eq.${tenantId}&select=tenant_id&limit=1`);
    if (bestaand?.length) {
      await sb.update("app_config", `tenant_id=eq.${tenantId}`, velden);
    } else {
      const row = await sb.insert("app_config", velden);
      if (row) setConfig(row); // nu mét id, zodat de volgende keer netjes geüpdatet wordt
    }
  };

  // Terug naar waar het recept vandaan werd geopend (bijv. de Mep-lijst).
  const terugNaarHerkomst = () => {
    if (!receptHerkomst) return;
    const h = receptHerkomst;
    setReceptHerkomst(null);
    setActiveModule(h.module);
    setSubView(h.subView ?? null);
  };

  const regelMepDashboard = async (item) => {
    const key = String(item?.mepKey || item?.tekst || "").trim().toLowerCase();
    if (!key || !tenantId) return;

    let mepData = dashboard.mepData;
    if (!mepData) {
      const rows = await sb.select("mep_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
      mepData = rows?.[0]?.data || null;
    }
    if (!mepData) return;

    const now = new Date();
    const { year, week } = dashIsoWeekOf(now);
    const weekKey = item?.weekKey || `${year}-W${String(week).padStart(2, "0")}`;
    const geregeld = { ...(mepData.whiteboard_geregeld || {}) };
    const lijst = Array.isArray(geregeld[weekKey]) ? geregeld[weekKey] : [];
    geregeld[weekKey] = lijst.includes(key) ? lijst : [...lijst, key];
    const next = { ...mepData, whiteboard_geregeld: geregeld };

    setDashboard((d) => ({
      ...d,
      mep: mepDashboardStats(next),
      mepWhiteboard: mepWhiteboardItems(next),
      mepData: next,
    }));
    try { window.dispatchEvent(new CustomEvent("kc:mep-data", { detail: next })); } catch {}
    await sb.update("mep_data", `tenant_id=eq.${tenantId}`, { data: next, updated_at: new Date().toISOString() });
  };

  // KC_DASHBOARD_PRINTER_STATUS
  const [printSystemStatus, setPrintSystemStatus] = useState({
    printer: undefined,
    printbox: undefined,
  });

  useEffect(() => {
    const baseUrl = String(config?.print_server_url || "").replace(/\/+$/, "");
    if (!baseUrl) {
      setPrintSystemStatus({ printer: undefined, printbox: undefined });
      return;
    }

    let actief = true;

    const controleer = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(`${baseUrl}/status`, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
        });
        const status = await response.json();
        if (!actief) return;

        if (!response.ok || status?.ok === false) {
          setPrintSystemStatus({ printer: undefined, printbox: false });
          return;
        }

        setPrintSystemStatus({
          printbox: true,
          printer:
            status?.printerOnline === true
              ? true
              : status?.printerOnline === false
                ? false
                : undefined,
        });
      } catch {
        if (actief) setPrintSystemStatus({ printer: undefined, printbox: false });
      } finally {
        clearTimeout(timeout);
      }
    };

    controleer();
    const interval = setInterval(controleer, 60000);
    return () => {
      actief = false;
      clearInterval(interval);
    };
  }, [config?.print_server_url]);

  const signOut = async () => { await supabaseClient.auth.signOut(); };

  // ── Render-gates ───────────────────────────────────────────────────────────
  if (!authReady) return <Status>{t("Laden…")}</Status>;
  if (recoveryMode) return <NieuwWachtwoordScherm supabaseClient={supabaseClient} onKlaar={() => setRecoveryMode(false)} />;
  if (!session) {
    if (inviteCode) {
      return <SignUp supabaseClient={supabaseClient} inviteCode={inviteCode} onInloggen={() => setAuthScherm("login")} />;
    }
    return authScherm === "signup"
      ? <SignUp supabaseClient={supabaseClient} onInloggen={() => setAuthScherm("login")} />
      : <LoginScreen supabaseClient={supabaseClient} onNaarSignup={() => setAuthScherm("signup")} />;
  }
  if (tenantError) return <Status onSignOut={signOut}>{tenantError}</Status>;
  if (!tenantId || loading) return <Status onSignOut={signOut}>{t("Je omgeving wordt geladen…")}</Status>;

  const uiPreviewExpired =
    typeof window !== "undefined" &&
    window.location.hostname.includes("deploy-preview-") &&
    new URLSearchParams(window.location.search).get("previewExpired") === "1";

  const subscriptionConfig = uiPreviewExpired
    ? { ...config, subscription_status: "expired" }
    : config;

  const subscription = subscriptionState(subscriptionConfig);
  if (subscription.blocked) {
    return (
      <SubscriptionGate
        config={subscriptionConfig}
        tenantId={tenantId}
        session={session}
        userRole={userRole}
        onLogout={signOut}
        previewMode={uiPreviewExpired}
      />
    );
  }

  const modules = MODULE_DEFS.filter((m) => {
    if (config?.modules && Object.keys(config.modules).length > 0)
      return config.modules[m.id] !== false;
    return true;
  });

  const bedrijfsnaam = config?.bedrijfsnaam || "KitchenControl";
  const logoUrl = config?.logo_url || "";
  const userEmail = session?.user?.email || "";
  const userNaam = session?.user?.user_metadata?.roepnaam || "";
  const mepStats = dashboard.mep;
  const mepPct = mepStats.totaal ? Math.round((mepStats.klaar / mepStats.totaal) * 100) : 0;
  const openTerugkoelStatus = openTerugkoelingen.map((r) => {
    const minuten = dashboardMinutenSindsStart(r, dashboardNow);
    return { ...r, minuten, teLang: minuten !== null && minuten > TERUGKOEL_MAX_MIN };
  });
  const openTerugkoelOverdue = openTerugkoelStatus.filter((r) => r.teLang);
  const feestdagReminder = komendeFeestdagReminder(10);

  // Nieuwe vereenvoudigde beginpagina. De bestaande modules blijven intact en
  // worden pas geopend zodra de gebruiker een onderdeel kiest.
  if (activeModule === null) {
    const dashboardWhiteboard = [...(dashboard.mepWhiteboard || []), ...(whiteboard.items || [])];
    return (
      <I18nProvider value={{ t, lang, setLang: setTaal }}>
        <BeheerPinProvider config={config} onConfigSave={handleConfigSave} supabaseClient={supabaseClient} session={session} userRole={userRole}>
        <KitchenDashboardShell
          activeModule={null}
          bedrijfsnaam={bedrijfsnaam}
          logoUrl={logoUrl}
          weatherPlace={config?.weer_plaats || ""}
          dashboardProps={{
            userNaam,
            mep: mepStats,
            recepten: receptDashboard,
            haccp: {
              dagelijksOpen: haccpSchoonmaakDashboard.dagelijksOpen,
              wekelijksOpen: haccpSchoonmaakDashboard.wekelijksOpen + haccpReminders.length,
              vierwekelijksOpen: haccpSchoonmaakDashboard.vierwekelijksOpen,
            },
            team: teamDashboard,
            whiteboard: dashboardWhiteboard,
            system: {
              teamCount: teamDashboard.length,
              printer: printSystemStatus.printer,
              printbox: printSystemStatus.printbox,
              subscriptionStatus: config?.subscription_status || null,
              subscriptionPlan: config?.subscription_plan || null,
            },
          }}
          onNavigate={(moduleId, view = null) => {
            if (!moduleId) { setActiveModule(null); setSubView(null); return; }
            setActiveModule(moduleId);
            if (moduleId === "mep") setSubView(view || "lijst");
            else if (moduleId === "recepten") setSubView(view || "recepten");
            else if (moduleId === "team") setSubView(null);
            else setSubView(view);
          }}
          onOpenWhiteboard={() => setWhiteboardOpen(true)}
          onOpenHelp={(topic = null) => { setHelpTopic(topic); setHelpOpen(true); }}
          language={{ lang, languages: TALEN, onChange: setTaal, label: t("Taal") }}
        />
        <WhiteboardSheet
          open={whiteboardOpen}
          onClose={() => setWhiteboardOpen(false)}
          items={dashboardWhiteboard}
          artikelen={whiteboard.artikelen}
          voegToe={whiteboard.voegToe}
          verwijder={whiteboard.verwijder}
          regelMep={regelMepDashboard}
        />
        <HelpPanel open={helpOpen} startTopic={helpTopic} onClose={() => { setHelpOpen(false); setHelpTopic(null); }} />
        </BeheerPinProvider>
      </I18nProvider>
    );
  }

  // ── Navigatie-niveaus (blokken, geen sidebar) ──────────────────────────────
  const def = activeModule ? MODULE_DEFS.find((m) => m.id === activeModule) : null;
  const hasSubs = (def?.subItems || []).length > 0;
  const toonOverzicht = !!def && hasSubs && (subView === null || subView === undefined);
  const subLabel = (def?.subItems || []).find((s) => s.id === subView)?.label || null;
  const huidigeKruimel = hasSubs ? subLabel : def?.label;
  const kruimelKnopStyle = { display: "inline-flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: P.pri, padding: "6px 9px", borderRadius: 7, whiteSpace: "nowrap" };
  const kruimelSepStyle = { color: P.sbTextMut, fontSize: 13, padding: "0 1px", userSelect: "none" };

  // Label van de herkomst (bijv. "Mep lijst") voor de terug-knop in de broodkruimel.
  const herkomstLabel = (() => {
    if (!receptHerkomst) return null;
    const m = MODULE_DEFS.find((x) => x.id === receptHerkomst.module);
    const s = (m?.subItems || []).find((x) => x.id === receptHerkomst.subView);
    return s?.label || m?.label || null;
  })();

  return (
    <I18nProvider value={{ t, lang, setLang: setTaal }}>
    <BeheerPinProvider config={config} onConfigSave={handleConfigSave} supabaseClient={supabaseClient} session={session} userRole={userRole}>
    <KitchenModuleShell
      activeModule={activeModule}
      bedrijfsnaam={bedrijfsnaam}
      logoUrl={logoUrl}
      weatherPlace={config?.weer_plaats || ""}
      language={{ lang, languages: TALEN, onChange: setTaal, label: t("Taal") }}
      onNavigate={(moduleId, view = null) => {
        setActiveModule(moduleId);
        if (moduleId === "mep") setSubView(view || "lijst");
        else if (moduleId === "recepten") setSubView(view || "recepten");
        else if (moduleId === "team") setSubView(null);
        else setSubView(view);
      }}
    >
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: P.bg, fontFamily: "system-ui,-apple-system,sans-serif", overflow: "hidden" }}>

      {activeModule === null ? (
        /* ══ BEGINPAGINA ══ */
        <div style={{ flex: 1, overflowY: "auto", padding: "clamp(28px, 5vw, 56px)", display: "flex", flexDirection: "column" }}>
          {/* Topbalk: logo/naam + e-mail links · taal/hulp rechts */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 30 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              {logoUrl
                ? <img src={logoUrl} alt={bedrijfsnaam} style={{ height: 36, maxWidth: 150, objectFit: "contain", flex: "none" }} />
                : (
                  <>
                    <img src="/kc-logo.png" alt="KitchenControl" style={{ height: 34, width: 34, borderRadius: "50%", flex: "none" }} />
                    <span style={{ fontSize: 18, fontWeight: 800, color: P.navy, letterSpacing: 0.3 }}>{bedrijfsnaam}</span>
                  </>
                )}
              <span style={{ fontSize: 12, color: P.sbTextMut, marginLeft: 4, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{userNaam || userEmail}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <select value={lang} onChange={(e) => setTaal(e.target.value)} aria-label={t("Taal")}
                style={{ fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: P.sbText, background: "#fff", border: `1px solid ${P.sbBorder}`, borderRadius: 8, padding: "6px 8px", cursor: "pointer" }}>
                {TALEN.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
              <HelpButton onClick={() => setHelpOpen(true)} />
            </div>
          </div>

          <h1 style={{ fontSize: 26, fontWeight: 800, color: P.navy, margin: "0 0 6px", letterSpacing: 0.2 }}>{t(groet())}{userNaam ? ", " + userNaam : ""}!</h1>
          <p style={{ fontSize: 15, color: P.sbTextMut, margin: "0 0 18px" }}>{t("Wat vraagt vandaag aandacht?")}</p>

          <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "minmax(0,1fr) minmax(280px,420px)", gap: 18, alignItems: "start" }}>
            <div style={{ minWidth: 0 }}>
              <button
                onClick={() => { setActiveModule("mep"); setSubView("lijst"); }}
                style={{ width: "100%", background: "#fff", border: `1px solid ${P.sbBorder}`, borderRadius: 14, padding: 18, textAlign: "left", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 1px 3px rgba(15,23,42,0.06)" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: P.priBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <ClipboardList size={22} color={P.pri} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: P.navy }}>{t("Mise En Place vandaag")}</div>
                    <div style={{ fontSize: 12.5, color: P.sbTextMut, marginTop: 2 }}>{mepStats.open} {t("open")} - {mepStats.klaar}/{mepStats.totaal} {t("klaar")}</div>
                  </div>
                  <ChevronRight size={22} color={P.txtMut} style={{ flexShrink: 0 }} />
                </div>
                <div style={{ height: 6, borderRadius: 999, background: "#E2E8F0", overflow: "hidden", marginTop: 16 }}>
                  <div style={{ width: `${mepPct}%`, height: "100%", borderRadius: 999, background: "#16A34A" }} />
                </div>
                {mepStats.klaarPerAfdeling.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 13 }}>
                    {mepStats.klaarPerAfdeling.slice(0, 3).map((afd) => (
                      <span key={afd.naam} style={{ fontSize: 11.5, fontWeight: 700, color: "#15803D", background: "#DCFCE7", border: "1px solid #BBF7D0", borderRadius: 999, padding: "4px 9px" }}>{afd.naam}: {t("klaar")}</span>
                    ))}
                  </div>
                )}
              </button>

              {feestdagReminder && (
                <button
                  onClick={() => { setActiveModule(null); setWhiteboardOpen(true); }}
                  style={{ width: "100%", marginTop: 10, display: "flex", alignItems: "center", gap: 12, background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 12, padding: "12px 14px", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <CalendarDays size={19} color="#B45309" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#92400E" }}>
                      {t("Feestdag in zicht")}: {t(feestdagReminder.naam)}
                    </div>
                    <div style={{ fontSize: 12, color: "#B45309", marginTop: 2 }}>
                      {feestdagReminder.dagen === 0 ? t("Vandaag") : t("Over {n} dagen", { n: feestdagReminder.dagen })} - {t("controleer bestel- en leverdagen")}
                    </div>
                  </div>
                  <ChevronRight size={18} color="#B45309" style={{ flexShrink: 0 }} />
                </button>
              )}

              <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "repeat(4, minmax(150px, 1fr))", gap: 14, marginTop: 16 }}>
                {modules.map((m) => {
                  const Icon = m.Icon;
                  const sub = m.id === "mep"
                    ? `${mepStats.open} ${t("open")} - ${mepPct}%`
                    : m.id === "recepten"
                      ? t("Recepten en allergenen")
                      : m.id === "team"
                        ? t("Medewerkers en rooster")
                        : m.id === "haccp"
                          ? t("Controles en rapportage")
                          : t("Beheer");
                  return (
                    <button
                      key={m.id}
                      onClick={() => { setActiveModule(m.id); setSubView(null); }}
                      style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 16, minHeight: 134, padding: 17, borderRadius: 14, background: "#fff", border: `1px solid ${P.sbBorder}`, cursor: "pointer", boxShadow: "0 1px 3px rgba(15,23,42,0.06)", fontFamily: "inherit", textAlign: "left" }}
                    >
                      <div style={{ width: 40, height: 40, borderRadius: 11, background: P.priBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {Icon && <Icon size={21} color={P.pri} />}
                      </div>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: P.navy }}>{t(m.label)}</div>
                        <div style={{ fontSize: 12, color: P.sbTextMut, marginTop: 4 }}>{sub}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {config?.weer_plaats
                ? <WeerWidget plaats={config.weer_plaats} />
                : <div style={{ background: "#fff", border: `1px solid ${P.sbBorder}`, borderRadius: 14, padding: 16, fontSize: 12.5, color: P.sbTextMut }}>{t("Stel je locatie in via Instellingen - Bedrijf om hier het weer te zien.")}</div>}
              <div style={{ background: "#fff", border: `1px solid ${P.sbBorder}`, borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: P.navy, marginBottom: 10 }}>{t("Snel naar")}</div>
                <button onClick={() => { setActiveModule(null); setWhiteboardOpen(true); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, background: "#F1F5F9", border: `1px solid ${P.sbBorder}`, borderRadius: 8, padding: "9px 10px", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, color: P.sbText, cursor: "pointer", textAlign: "left", marginBottom: 8 }}>
                  <FileText size={15} /> {t("Whiteboard")}
                </button>
                <button onClick={() => { setActiveModule("haccp"); setSubView("temp"); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, background: "#F1F5F9", border: `1px solid ${P.sbBorder}`, borderRadius: 8, padding: "9px 10px", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, color: P.sbText, cursor: "pointer", textAlign: "left" }}>
                  <Thermometer size={15} /> {t("Temperatuur")}
                </button>
              </div>
              {openTerugkoelStatus.length > 0 && (
                <div style={{ background: "#fff", border: `1px solid ${openTerugkoelOverdue.length ? "#FCA5A5" : P.sbBorder}`, borderRadius: 14, padding: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <Snowflake size={16} color={openTerugkoelOverdue.length ? "#DC2626" : P.pri} />
                    <div style={{ fontSize: 13, fontWeight: 800, color: P.navy }}>{t("Lopende terugkoeling")}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {openTerugkoelStatus.slice(0, toonAlleTerugkoelingen ? openTerugkoelStatus.length : 2).map((r) => (
                      <button
                        key={r.id}
                        onClick={() => { setActiveModule("haccp"); setSubView("terugkoel"); }}
                        style={{ width: "100%", display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center", background: r.teLang ? "#FEF2F2" : "#F8FAFC", border: `1px solid ${r.teLang ? "#FCA5A5" : P.sbBorder}`, borderRadius: 9, padding: "9px 10px", textAlign: "left", fontFamily: "inherit", cursor: "pointer" }}
                      >
                        <span style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 800, color: P.navy }}>{r.product || t("Terugkoeling")}</div>
                          <div style={{ fontSize: 11.5, color: r.teLang ? "#DC2626" : P.sbTextMut, marginTop: 2 }}>
                            {r.teLang ? t("Langer dan 5 uur bezig") : t("Bezig")} · {dashboardDuurTekst(r.minuten)}
                          </div>
                        </span>
                        <ChevronRight size={16} color={P.txtMut} />
                      </button>
                    ))}
                    {openTerugkoelStatus.length > 2 && (
                      <button
                        type="button"
                        onClick={() => setToonAlleTerugkoelingen((v) => !v)}
                        style={{ alignSelf: "flex-start", background: "none", border: 0, padding: "2px 4px", fontFamily: "inherit", fontSize: 11.5, fontWeight: 700, color: P.pri, cursor: "pointer" }}
                      >
                        {toonAlleTerugkoelingen ? t("Minder tonen") : t("+ {n} meer", { n: openTerugkoelStatus.length - 2 })}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {haccpReminders.length > 0 && (
                <div style={{ background: "#fff", border: `1px solid ${P.sbBorder}`, borderRadius: 14, padding: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <ShieldCheck size={16} color={P.pri} />
                    <div style={{ fontSize: 13, fontWeight: 800, color: P.navy }}>{t("HACCP aandacht")}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {haccpReminders.slice(0, toonAlleHaccpReminders ? haccpReminders.length : 3).map(reminder => (
                      <button
                        key={reminder.id}
                        onClick={() => { setActiveModule("haccp"); setSubView(reminder.subView); }}
                        style={{ width: "100%", display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center", background: "#F8FAFC", border: `1px solid ${P.sbBorder}`, borderRadius: 9, padding: "9px 10px", textAlign: "left", fontFamily: "inherit", cursor: "pointer" }}
                      >
                        <span style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 800, color: P.navy }}>{t(reminder.label)}</div>
                          <div style={{ fontSize: 11.5, color: P.sbTextMut, marginTop: 2 }}>{t("Deze week nog niet geregistreerd")}</div>
                        </span>
                        <ChevronRight size={16} color={P.txtMut} />
                      </button>
                    ))}
                    {haccpReminders.length > 3 && (
                      <button
                        type="button"
                        onClick={() => setToonAlleHaccpReminders((v) => !v)}
                        style={{ alignSelf: "flex-start", background: "none", border: 0, padding: "2px 4px", fontFamily: "inherit", fontSize: 11.5, fontWeight: 700, color: P.pri, cursor: "pointer" }}
                      >
                        {toonAlleHaccpReminders ? t("Minder tonen") : t("+ {n} meer", { n: haccpReminders.length - 3 })}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: "auto", paddingTop: 34, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 11, letterSpacing: 0.4, color: P.sbTextMut }}>
              {t("powered by")} <span style={{ fontWeight: 700, color: P.sbText }}>KitchenControl</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ fontSize: 12, color: "#16A34A", fontWeight: 600 }}>✓ {t("verbonden")}</span>
              <button onClick={signOut}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${P.sbBorder}`, color: P.sbText, cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", padding: "7px 13px", borderRadius: 8 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = P.sbHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}>
                <LogOut size={14} /> {t("Uitloggen")}
              </button>
            </div>
          </div>
        </div>

      ) : toonOverzicht ? (
        /* ══ MODULE-OVERZICHT (blokken) ══ */
        <ModuleOverzicht
          def={def}
          userRole={userRole}
          onBack={() => { setActiveModule(null); setSubView(null); }}
          onPick={(id) => setSubView(id)}
        />

      ) : (
        /* ══ SUB-WEERGAVE (de tool zelf) ══ */
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Broodkruimel-balk: Begin › module › huidige stap */}
          <div style={{ flexShrink: 0, height: 44, display: "flex", alignItems: "center", padding: "0 6px", background: P.sbBg, borderBottom: `1px solid ${P.sbBorder}`, overflowX: "auto" }}>
            <nav style={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
              {/* Terug-knop naar de herkomst (bijv. de Mep-lijst) — alleen zichtbaar
                  zolang je een recept bekijkt dat je daar opende. */}
              {receptHerkomst && herkomstLabel && (
                <button
                  onClick={terugNaarHerkomst}
                  title={t("Terug")}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, background: P.priBg, border: `1px solid ${P.sbBorder}`, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, color: P.pri, padding: "6px 11px", borderRadius: 8, whiteSpace: "nowrap", marginRight: 8 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#DBEAFE"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = P.priBg; }}>
                  <ChevronLeft size={15} /> {t(herkomstLabel)}
                </button>
              )}
              <button
                onClick={() => { setActiveModule(null); setSubView(null); }}
                style={kruimelKnopStyle}
                onMouseEnter={(e) => { e.currentTarget.style.background = P.sbHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}>
                {t("Begin")}
              </button>
              {hasSubs && (
                <>
                  <span style={kruimelSepStyle}>›</span>
                  <button
                    onClick={() => setSubView(null)}
                    style={kruimelKnopStyle}
                    onMouseEnter={(e) => { e.currentTarget.style.background = P.sbHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}>
                    {t(def.label)}
                  </button>
                </>
              )}
              {huidigeKruimel && (
                <>
                  <span style={kruimelSepStyle}>›</span>
                  <span style={{ padding: "6px 9px", fontWeight: 700, color: P.navy, whiteSpace: "nowrap" }}>{t(huidigeKruimel)}</span>
                </>
              )}
            </nav>
          </div>

          {/* Module-inhoud */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {activeModule === "team" ? (
              <TeamModule activeTab={subView} onTabChange={setSubView} sb={sb} tenantId={tenantId} supabaseClient={supabaseClient} userRole={userRole} userEmail={session?.user?.email} />
            ) : activeModule === "haccp" ? (
              <HaccpModule coolingRequest={coolingRequest?.tenantId === tenantId ? coolingRequest : null} onCoolingRequestConsumed={() => setCoolingRequest(null)} sb={sb} tenantId={tenantId} supabaseClient={supabaseClient} activeTab={subView} onTabChange={setSubView} userRole={userRole} />
            ) : activeModule === "recepten" ? (
              <ReceptenModule activeTab={subView} onTabChange={setSubView} sb={sb} tenantId={tenantId} supabaseClient={supabaseClient} openReceptId={openReceptId} onOpenReceptHandled={() => setOpenReceptId(null)} userRole={userRole} />
            ) : activeModule === "mep" ? (
              <MepModule
                onStartCooling={(item, active) => {
                  setCoolingRequest(active ? null : { product: item.naam, itemId: String(item.id), tenantId });
                  setActiveModule("haccp");
                  setSubView("terugkoel");
                }}
                config={config}
                synced={!loading}
                onConfigSave={handleConfigSave}
                activeTab={subView}
                onTabChange={setSubView}
                onOpenRecept={(receptId) => { setReceptHerkomst({ module: activeModule, subView }); setOpenReceptId(receptId); setActiveModule("recepten"); setSubView("recepten"); }}
                sb={sb}
                tenantId={tenantId}
                supabaseClient={supabaseClient}
                userRole={userRole}
              />
            ) : activeModule === "settings" ? (
              <SettingsModule sb={sb} tenantId={tenantId} config={config} onConfigSave={handleConfigSave} supabaseClient={supabaseClient} session={session} userRole={userRole} />
            ) : null}
          </div>
        </div>
      )}

      {/* Hulp: ?-knop rechtsboven op module- en sub-schermen, plus het hulp-paneel */}
      {activeModule !== null && (
        <div style={{ position: "fixed", top: 7, right: 12, zIndex: 40 }}>
          <HelpButton compact onClick={() => setHelpOpen(true)} />
        </div>
      )}
      <HelpPanel open={helpOpen} startTopic={helpTopic} onClose={() => { setHelpOpen(false); setHelpTopic(null); }} />
    </div>
    </KitchenModuleShell>
    </BeheerPinProvider>
    </I18nProvider>
  );
}

